import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Check, ChevronDown, Target } from 'lucide-react';
import useApi from '../api';
import { formatDate } from '../utils/dateFormatter';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import SectionHeader from '@/components/ui/SectionHeader';

interface ObjectiveInstanceView {
  status: 'in_progress' | 'done';
  status_source: string;
  status_updated_at?: string | null;
}

interface ContactObjectiveItem {
  id: string;
  name: string;
  description: string;
  instance?: ObjectiveInstanceView | null;
}

const STATUS_LABELS: Record<string, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  done: 'Done',
  skipped: 'Skipped',
};

const STATUS_TONES: Record<string, string> = {
  not_started: 'bg-muted text-muted-foreground',
  in_progress: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
  done: 'bg-green-500/10 text-green-700 dark:text-green-400',
  skipped: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
};

const SOURCE_LABELS: Record<string, string> = {
  ai: 'the AI agent',
  sdk: 'your SDK',
  manual: 'a teammate',
  crm: 'CRM sync',
};

function StatusBadge({
  status,
  source,
  updatedAt,
  interactive,
}: {
  status: string;
  source?: string;
  updatedAt?: string | null;
  interactive?: boolean;
}) {
  const badge = (
    <span className={`inline-flex items-center gap-1 rounded-xl px-2 py-0.5 text-xs font-semibold ${STATUS_TONES[status] || STATUS_TONES.not_started}`}>
      {STATUS_LABELS[status] || status}
      {interactive && <ChevronDown className="h-3 w-3" />}
    </span>
  );
  if (!source) return badge;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent>
          Set by {SOURCE_LABELS[source] || source}{updatedAt ? ` · ${formatDate(updatedAt)}` : ''}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function ContactObjectives({ leadId }: { leadId: string }) {
  const { get, patch } = useApi();
  const [items, setItems] = useState<ContactObjectiveItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    get(`/leads/${leadId}/objectives`)
      .then((response) => {
        if (!cancelled) setItems(response.data?.items || []);
      })
      .catch((error) => console.error('Error fetching contact objectives:', error))
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [get, leadId]);

  const setStatus = async (item: ContactObjectiveItem, status: 'in_progress' | 'done') => {
    try {
      const response = await patch(`/leads/${leadId}/objectives/${item.id}`, { status });
      setItems((current) => current.map((row) => row.id === item.id ? response.data : row));
    } catch (error: any) {
      toast.error(error?.data?.detail || 'Failed to update objective');
    }
  };

  if (!loaded || !items.length) return null;

  const completed = items.filter((item) => item.instance?.status === 'done').length;

  return (
    <div className="space-y-2">
      <SectionHeader
        title="Objectives"
        icon={Target}
        trailing={(
          <span className="pt-1 text-xs font-medium tabular-nums text-muted-foreground">
            {completed} of {items.length} completed
          </span>
        )}
      />
      <div className="divide-y rounded-xl border bg-card">
        {items.map((item) => {
          const status = item.instance?.status || 'not_started';
          return (
            <div key={item.id} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{item.name}</div>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">{item.description}</p>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="transition-opacity hover:opacity-80">
                    <StatusBadge
                      status={status}
                      source={item.instance?.status_source}
                      updatedAt={item.instance?.status_updated_at}
                      interactive
                    />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {(['in_progress', 'done'] as const).map((nextStatus) => (
                    <DropdownMenuItem
                      key={nextStatus}
                      className="gap-2"
                      onClick={() => nextStatus !== status && setStatus(item, nextStatus)}
                    >
                      <StatusBadge status={nextStatus} />
                      {nextStatus === status && <Check className="ml-auto h-3.5 w-3.5" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        })}
      </div>
    </div>
  );
}
